<script setup lang="ts">

import {useDebounceFn, watchIgnorable} from "@vueuse/core";
import {onMounted, type Ref, ref, toRaw} from "vue";
import { date, number, object, string } from 'yup';

import type {ProductResponse} from "../../../../responses/ProductResponse.ts";
import type {ContactDetailsResponse} from "../../../../responses/Site/ContactDetailsResponse.ts";
import {label} from "../../../mixins/translations.ts";
import {useEnrollmentStore} from "../../../stores/enrollment.ts";
import FloatingInput from "../../../ui/inputs/FloatingInput.vue";
import ContactDetails from "./ContactDetails.vue";

interface Props {
    modelValue: ContactDetailsResponse;
    errors: { [key: string]: string };
}

const props = withDefaults(defineProps<Props>(), {
    errors: {}
});

const emit = defineEmits(['update:modelValue']);
const store = useEnrollmentStore();


const contactDetails: Ref<ContactDetailsResponse> = ref({});
const success: Ref<{ [key: string]: boolean }> = ref({});

const validateField = async (fieldName, formData) => {
    contactDetails.value[fieldName] = contactDetails.value[fieldName]?.trim();
    try {
        await store.contactDetailsValidation.validateAt(fieldName, formData);

        if(fieldName === 'insertion' && contactDetails.value[fieldName] === '') return delete contactDetails.value[fieldName];
        success.value[fieldName] = true;
        delete props.errors[fieldName];
    } catch (error) {
        props.errors[fieldName] = error.message;
        success.value[fieldName] = false;
    }
}

const showContactDetails: Ref<boolean> = ref(false);

const toggleContactDetails = (show : boolean) => {
    if(show) {
        showContactDetails.value = true;
        contactDetails.value = {
            initials: '',
            last_name: '',
            first_name: '',
            email: '',
            phone: '',
        }
    } else {
        showContactDetails.value = false;
        contactDetails.value = {};
        success.value  = {};
    }
}


const {ignoreUpdates} = watchIgnorable(() => contactDetails.value, useDebounceFn((newValue) => {
    emit('update:modelValue', newValue)
}, 500), {deep: true})

onMounted(() => {
    ignoreUpdates(() => {
        contactDetails.value = props.modelValue;
       validate(true);
    })

    if(Object.keys(contactDetails.value).length !== 0) {
        showContactDetails.value = true;
    }
});

const validate = (initial = false) => {
    Object.entries(contactDetails.value).forEach(([key, value]) => {
        if(!initial) {  //also check if empty
            validateField(key, contactDetails.value);
        } else {
            if (value) {
                validateField(key, contactDetails.value);
            }
        }
    });
}

defineExpose({ validate })

</script>

<template>
    <div class="py-2">
        <div class=" flex items-center gap-4">
            <span class="text-gray-900 text-base leading-relaxed" v-text="label('site.enrollment.enroll_who')"></span>
            <div class="flex rounded-full border border-gray-300 p-1 bg-white">
                <button
                    @click.prevent="toggleContactDetails(false)"
                    :class="{
                        'bg-primary text-white' : !showContactDetails
                    }" type="button" class="px-4 py-1.5 rounded-full text-sm font-semibold text-gray-700" v-text="label('site.enrollment.myself')">
                </button>
                <button
                    @click.prevent="toggleContactDetails(true)"
                    type="button"
                    :class="{
                            'bg-primary text-white' : showContactDetails
                        }" class="px-4 py-1.5 text-sm rounded-full font-semibold text-gray-700" v-text="label('site.enrollment.someone_else')">
                </button>
            </div>
        </div>

        <div v-if="showContactDetails"  class="flex flex-col gap-4 pb-3 border-b border-gray-300">
            <span class="mb-2 text-base leading-relaxed" v-text="label('site.enrollment.contact_data')"></span>
            <div class="grid gap-4 md:grid-cols-3">
                <FloatingInput v-model="contactDetails.initials" @focusout="validateField('initials', contactDetails)" @change="validateField('initials', contactDetails)" :error="errors.initials" :success="success.initials" :label="`${label('site.initials')} *`" />
                <FloatingInput v-model="contactDetails.insertion" @change="validateField('insertion', contactDetails)"  :error="errors.insertion" :success="success.insertion" :label="`${label('site.insertion')}`"  />
                <FloatingInput v-model="contactDetails.last_name" @focusout="validateField('last_name', contactDetails)" @change="validateField('last_name', contactDetails)" :error="errors.last_name" :success="success.last_name"  :label="`${label('site.last_name')} *`"  />
            </div>
            <FloatingInput v-model="contactDetails.first_name" @focusout="validateField('last_name', contactDetails)" @change="validateField('first_name', contactDetails)" :error="errors.first_name" :success="success.first_name"  :label="`${label('site.first_name')} *`"  />
            <div class="grid gap-4 md:grid-cols-2">
            <FloatingInput v-model="contactDetails.email" @focusout="validateField('email', contactDetails)" @change="validateField('email', contactDetails)"  :error="errors.email" :success="success.email" :label="`${label('site.email_address')} *`"  />
            <FloatingInput type="tel" v-model="contactDetails.phone" @focusout="validateField('phone', contactDetails)" @change="validateField('phone', contactDetails)"  :error="errors.phone" :success="success.phone" :label="`${label('site.phone_number')} *`"  />
            </div>
        </div>
    </div>
</template>

